import { useEffect, useMemo, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { api } from "@/lib/api";
import { MapPin, Clock, Shield, Footprints, Play, Stop } from "@phosphor-icons/react";
import StoreFloorPlan from "@/components/StoreFloorPlan";

const fmtDur = (s) => {
  s = Math.max(0, Math.round(s || 0));
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  if (h) return `${h}h ${m}m`;
  if (m) return `${m} min`;
  return `${s} sec`;
};
const fmtTime = (iso) => {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleTimeString([], { hour: "numeric", minute: "2-digit" }); }
  catch { return "—"; }
};
const DOT = ["#34C759", "#0A84FF", "#BF5AF2", "#FF9F0A", "#FF375F", "#64D2FF", "#FFD60A"];
const color = (i) => DOT[i % DOT.length];
const initials = (n) => (n || "?").split(" ").map((w) => w[0]).slice(0, 2).join("").toUpperCase();
// Store runs on Chicago time (backend STORE_TZ=America/Chicago). Default the date to the
// store's local day — not the viewer's UTC/browser day — so "today" always matches the backend.
const STORE_TZ = "America/Chicago";
const storeToday = () => new Intl.DateTimeFormat("en-CA", { timeZone: STORE_TZ }).format(new Date());

export default function MovementMap() {
  const [employees, setEmployees] = useState([]);
  const [pid, setPid] = useState("");
  const [date, setDate] = useState(storeToday);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [live, setLive] = useState(false);
  const [, setTick] = useState(0);
  const [progress, setProgress] = useState(1);
  const startRef = useRef(null);
  const rafRef = useRef(null);

  useEffect(() => {
    api.get("/employees").then((r) => {
      const list = (r.data?.employees || []).filter((e) => e.name);
      setEmployees(list);
      if (list[0]) setPid((p) => p || list[0].id);
    }).catch(() => setEmployees([]));
  }, []);

  const fetchTrail = () => {
    if (!pid) return;
    api.get(`/employees/${pid}/trail?date=${date}`).then((r) => setData(r.data)).catch(() => {});
  };
  useEffect(() => {
    if (!pid) return;
    setLoading(true);
    api.get(`/employees/${pid}/trail?date=${date}`)
      .then((r) => setData(r.data)).catch(() => setData(null)).finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pid, date]);
  useEffect(() => {
    if (!live) return;
    const t = setInterval(() => { fetchTrail(); setTick((n) => n + 1); }, 4000);
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [live, pid, date]);

  const toggleLive = () => {
    if (live) { setLive(false); return; }
    startRef.current = Date.now();
    setLive(true);
    fetchTrail();
  };
  const elapsed = live && startRef.current ? Math.floor((Date.now() - startRef.current) / 1000) : 0;

  // Replay: draw the day's path from start to finish like a GPS hike track.
  useEffect(() => { setProgress(1); }, [pid, date]);
  useEffect(() => () => cancelAnimationFrame(rafRef.current), []);
  const replayWalk = () => {
    cancelAnimationFrame(rafRef.current);
    const nStops = (data?.segments || []).length;
    const dur = Math.max(2500, Math.min(10000, nStops * 650));
    const t0 = performance.now();
    setProgress(0);
    const step = (now) => {
      const t = Math.min(1, (now - t0) / dur);
      setProgress(t);
      if (t < 1) rafRef.current = requestAnimationFrame(step);
    };
    rafRef.current = requestAnimationFrame(step);
  };

  const stops = data?.segments || [];
  const summary = data?.summary || {};
  const byZone = summary.by_zone || {};
  const maxZone = useMemo(() => Math.max(1, ...Object.values(byZone)), [byZone]);
  const person = data?.person;

  return (
    <div className="max-w-5xl mx-auto space-y-6" data-testid="movement-map-page">
      {/* Friendly header + controls */}
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <div className="w-11 h-11 rounded-2xl bg-[#0A84FF]/15 flex items-center justify-center">
            <Footprints size={24} weight="fill" className="text-[#0A84FF]" />
          </div>
          <div>
            <h1 className="font-display font-bold text-2xl tracking-tight">Where did they go?</h1>
            <p className="text-sm text-[#A1A1AA]">A simple map of each person's day around the store.</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <select
            data-testid="movement-employee-select"
            value={pid}
            onChange={(e) => setPid(e.target.value)}
            className="bg-[#17171A] border border-white/10 rounded-full text-white text-sm px-4 py-2 outline-none focus:border-[#0A84FF]"
          >
            {employees.length === 0 && <option value="">No one enrolled yet</option>}
            {employees.map((e) => <option key={e.id} value={e.id}>{e.name}</option>)}
          </select>
          <input
            data-testid="movement-date-input"
            type="date"
            value={date}
            onChange={(e) => setDate(e.target.value)}
            className="bg-[#17171A] border border-white/10 rounded-full text-white text-sm px-4 py-2 outline-none focus:border-[#0A84FF]"
          />
          <motion.button
            data-testid="walk-test-toggle"
            whileTap={{ scale: 0.95 }}
            onClick={toggleLive}
            className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm font-semibold transition-colors ${
              live ? "bg-[#FF375F] text-white" : "bg-[#34C759] text-black hover:bg-[#2fb850]"
            }`}
          >
            {live ? <Stop size={16} weight="fill" /> : <Play size={16} weight="fill" />}
            {live ? `Stop · ${Math.floor(elapsed / 60)}:${String(elapsed % 60).padStart(2, "0")}` : "Walk Test"}
          </motion.button>
        </div>
      </div>

      {/* Live banner */}
      <AnimatePresence>
        {live && (
          <motion.div
            data-testid="walk-test-live"
            initial={{ opacity: 0, y: -8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -8 }}
            className="rounded-2xl bg-[#34C759]/12 border border-[#34C759]/30 px-5 py-3 flex flex-wrap items-center gap-x-4 gap-y-1"
          >
            <span className="flex items-center gap-2 font-semibold text-[#34C759]">
              <span className="w-2.5 h-2.5 rounded-full bg-[#34C759] live-dot" /> Following live
            </span>
            <span className="text-sm text-[#A1A1AA]">Walk around — each stop pops in as a camera spots you.</span>
            <span className="ml-auto text-sm text-white">{summary.cameras_seen || 0} cameras · {summary.zones_visited || 0} areas</span>
          </motion.div>
        )}
      </AnimatePresence>

      {loading && <div className="text-center text-[#71717A] text-sm py-16">Loading…</div>}

      {/* Spatial floor plan — always visible; lights up the areas this person visited */}
      {!loading && (
        <div className="space-y-3">
          {stops.length > 1 && !live && (
            <div className="flex items-center justify-between px-1">
              <p className="text-xs uppercase tracking-widest text-[#71717A]">Walking trail · {stops.length} stops</p>
              <button
                data-testid="trail-replay"
                onClick={replayWalk}
                className="flex items-center gap-2 px-4 py-2 rounded-full text-sm font-semibold bg-[#0A84FF] text-white hover:bg-[#3395FF] transition-colors"
              >
                <Play size={15} weight="fill" /> {progress < 1 ? "Replaying…" : "Replay walk"}
              </button>
            </div>
          )}
          <StoreFloorPlan
            activeZones={stops.map((s) => s.zone)}
            currentZone={live && stops.length ? stops[stops.length - 1].zone : ""}
            trail={stops.map((s) => s.zone)}
            trailProgress={progress}
          />
        </div>
      )}

      {/* Owner privacy */}
      {!loading && data?.no_track && (
        <motion.div
          data-testid="movement-notrack"
          initial={{ opacity: 0, scale: 0.98 }} animate={{ opacity: 1, scale: 1 }}
          className="rounded-2xl border border-[#34C759]/30 bg-[#34C759]/10 px-6 py-8 flex items-center gap-4"
        >
          <Shield size={30} weight="fill" className="text-[#34C759]" />
          <div>
            <p className="font-semibold text-[#34C759]">Private — not tracked</p>
            <p className="text-sm text-[#A1A1AA] mt-0.5">{person?.name} is never mapped or scored. That's the owner setting.</p>
          </div>
        </motion.div>
      )}

      {!loading && data && !data.no_track && (
        <>
          {/* Person + plain-English summary */}
          <motion.div
            initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}
            className="rounded-2xl bg-[#17171A] border border-white/10 p-5 flex items-center gap-4"
          >
            <div className="w-14 h-14 rounded-2xl overflow-hidden bg-[#0A84FF]/15 flex items-center justify-center shrink-0">
              {person?.rep_image_b64
                ? <img src={`data:image/jpeg;base64,${person.rep_image_b64}`} alt="" className="w-full h-full object-cover" />
                : <span className="font-bold text-lg text-[#0A84FF]">{initials(person?.name)}</span>}
            </div>
            <div>
              <p className="font-display font-bold text-lg">{person?.name}</p>
              <p className="text-sm text-[#A1A1AA]">
                {stops.length === 0
                  ? "No movement yet today."
                  : <>On the floor for <b className="text-white">{fmtDur(summary.total_tracked_seconds)}</b> · visited <b className="text-white">{summary.zones_visited}</b> {summary.zones_visited === 1 ? "area" : "areas"} · first seen <b className="text-white">{fmtTime(summary.first_seen)}</b></>}
              </p>
            </div>
          </motion.div>

          {stops.length === 0 ? (
            <motion.div
              data-testid="movement-empty"
              initial={{ opacity: 0 }} animate={{ opacity: 1 }}
              className="rounded-2xl border border-dashed border-white/15 p-12 text-center"
            >
              <Footprints size={34} className="text-[#3A3A3E] mx-auto mb-3" />
              <p className="text-sm text-[#71717A] max-w-sm mx-auto">
                Nothing to show yet. As soon as a camera recognises {person?.name}, their stops appear here.
              </p>
            </motion.div>
          ) : (
            <>
              {/* The journey — animated stops */}
              <div className="rounded-2xl bg-[#17171A] border border-white/10 p-6" data-testid="movement-timeline">
                <p className="text-xs uppercase tracking-widest text-[#71717A] mb-5">Their journey</p>
                <div className="relative">
                  <div className="absolute left-5 top-2 bottom-2 w-0.5 bg-white/8" />
                  <div className="space-y-4">
                    {stops.map((s, i) => (
                      <motion.div
                        key={`${s.zone}-${s.start}`}
                        initial={{ opacity: 0, x: -16 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ delay: Math.min(i * 0.08, 0.6), type: "spring", stiffness: 260, damping: 22 }}
                        className="relative flex items-center gap-4 pl-1"
                        data-testid={`movement-segment-${i}`}
                      >
                        <motion.span
                          className="relative z-10 w-9 h-9 rounded-full flex items-center justify-center shrink-0"
                          style={{ background: `${color(i)}22` }}
                          animate={live && i === stops.length - 1 ? { scale: [1, 1.18, 1] } : {}}
                          transition={{ repeat: Infinity, duration: 1.4 }}
                        >
                          <MapPin size={18} weight="fill" style={{ color: color(i) }} />
                        </motion.span>
                        <div className="flex-1 flex flex-wrap items-center justify-between gap-1 border-b border-white/5 pb-3">
                          <div>
                            <p className="font-semibold text-white capitalize">{s.zone.toLowerCase()}</p>
                            <p className="text-xs text-[#71717A]">{fmtTime(s.start)}{s.cameras?.length ? ` · ${s.cameras.length} cam${s.cameras.length > 1 ? "s" : ""}` : ""}</p>
                          </div>
                          <span className="text-sm font-semibold px-3 py-1 rounded-full" style={{ background: `${color(i)}18`, color: color(i) }}>
                            {fmtDur(s.dwell_seconds)}
                          </span>
                        </div>
                      </motion.div>
                    ))}
                  </div>
                </div>
              </div>

              {/* Time per area — simple bars */}
              <div className="rounded-2xl bg-[#17171A] border border-white/10 p-6" data-testid="movement-by-zone">
                <p className="text-xs uppercase tracking-widest text-[#71717A] mb-5">Time spent in each area</p>
                <div className="space-y-4">
                  {Object.entries(byZone).sort((a, b) => b[1] - a[1]).map(([z, sec], i) => (
                    <div key={z} data-testid={`movement-zone-${z}`}>
                      <div className="flex justify-between text-sm mb-1.5">
                        <span className="text-white capitalize">{z.toLowerCase()}</span>
                        <span className="text-[#A1A1AA]">{fmtDur(sec)}</span>
                      </div>
                      <div className="h-2.5 bg-white/5 rounded-full overflow-hidden">
                        <motion.div
                          className="h-full rounded-full"
                          style={{ background: color(i) }}
                          initial={{ width: 0 }}
                          animate={{ width: `${(sec / maxZone) * 100}%` }}
                          transition={{ delay: 0.1 + i * 0.07, duration: 0.6, ease: "easeOut" }}
                        />
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </>
          )}
        </>
      )}
    </div>
  );
}
